/**
 * OA-471 §1/T3 — `validateNumericField`: shared, reusable NON-money numeric
 * validation. The sibling of `useCentsField` (money): where `useCentsField`
 * owns dollar⇄cents conversion + persistent hook state, this is a tiny PURE
 * function set for plain numeric scalar fields (years, useful-life, sqft/acres).
 *
 * DELIBERATELY NOT a hook and NOT money-aware:
 *  - No React, no `useDemo()`, no async resync. Non-money scalar fields use
 *    AssetDetail's existing pencil-click pattern — local edit state is seeded
 *    from `asset` only at the click, and when not editing the field reads
 *    `asset.field` directly, so a server refresh is reflected with no resync
 *    logic. `useCentsField` needs resync because it holds persistent conversion
 *    state; plain fields do not. Do NOT port that machinery here.
 *
 * Bounds match the REAL backend Pydantic models (ourhomebase-api `family.py`,
 * verified — NOT the stale `backend/` decoy tree, NOT invented):
 *  - StrictYear (`family.py:75-81`): `>= 1950`, `<= current calendar year + 1`,
 *    the upper bound computed DYNAMICALLY at validation time — never a hardcoded
 *    ceiling like 2100. Applies to `year_built`, `roof_install_year`,
 *    `hvac_install_year`, `plumbing_update_year`, `electrical_update_year`,
 *    `water_heater_install_year`, `last_filing_tax_year`, and tax-assessment
 *    row `year`.
 *  - `depreciation_useful_life_years`: `Field(ge=1, le=100)` — 1..100 inclusive.
 *  - `above_grade_sqft` / `finished_basement_sqft` / `lot_size_acres`:
 *    `Field(ge=0)` — non-negative, no hard upper bound.
 *
 * These bounds and messages intentionally MIRROR the inline checks T2 landed in
 * `useAssetDetail.js#parseScalarField`, so that hook can later import these and
 * delete its duplicate inline copies with ZERO behavior change (see notes.md —
 * that consolidation is out of THIS task's ownership boundary).
 *
 * Every validator returns `{ value, error, isBlank }`, mirroring
 * `parseDollarInput` so both families of scalar fields read the same at the
 * call site:
 *   - blank input       → { value: null, error: null,      isBlank: true }
 *                         (caller decides: for §1 optional fields, "no change,
 *                          don't submit this field" — NEVER coerced to 0)
 *   - out-of-bounds     → { value: null, error: <message>,  isBlank: false }
 *                         (REJECTED client-side — never silently clamped)
 *   - valid             → { value: <number>, error: null,   isBlank: false }
 */

/** True when the raw input is null/undefined/whitespace-only. */
function isBlankInput(raw) {
  return raw == null || String(raw).trim() === "";
}

/**
 * The dynamic StrictYear upper bound: current calendar year + 1, evaluated at
 * call time so it never goes stale (no hardcoded ceiling). Exported so a call
 * site can render "1950–{max}" hints without re-deriving the rule.
 */
export function strictYearMax() {
  return new Date().getFullYear() + 1;
}

/**
 * Generic inclusive numeric-range validator.
 *
 * @param {*} raw                     raw user input (string or number)
 * @param {object} [opts]
 * @param {number|null} [opts.min]    inclusive lower bound (null = no lower bound)
 * @param {number|null} [opts.max]    inclusive upper bound (null = no upper bound)
 * @param {boolean} [opts.integer]    require an integer (default false — allows
 *                                    decimals, e.g. 27.5 useful-life years / acres)
 * @param {string} [opts.message]     error message when out of range / non-numeric
 * @returns {{ value: number|null, error: string|null, isBlank: boolean }}
 */
export function validateNumericField(raw, opts = {}) {
  const { min = null, max = null, integer = false, message } = opts;
  if (isBlankInput(raw)) return { value: null, error: null, isBlank: true };

  const n = Number(String(raw).trim());
  const numericOk = integer ? Number.isInteger(n) : Number.isFinite(n);
  const inRange =
    numericOk && (min == null || n >= min) && (max == null || n <= max);

  if (!inRange) {
    return { value: null, error: message || "Enter a valid number.", isBlank: false };
  }
  return { value: n, error: null, isBlank: false };
}

/**
 * StrictYear convenience wrapper: `>= 1950`, `<= current year + 1` (dynamic),
 * integer-only. Message intentionally identical to the inline check T2 shipped.
 */
export function validateYearField(raw) {
  const max = strictYearMax();
  return validateNumericField(raw, {
    min: 1950,
    max,
    integer: true,
    message: `Enter a year between 1950 and ${max}.`,
  });
}

export default validateNumericField;
